/**
 * components/landing/trading-platform/sections/Hero.jsx — §5 Hero.
 * Trading terminal cinematic hero with mini-chart, watchlist and AI insight.
 */

import React, { useMemo } from 'react'
import { PlayCircle, ArrowUpRight, ArrowDownRight, Bot, Sparkles } from 'lucide-react'
import { Container, Eyebrow, GradientButton, GhostButton } from '@/components/landing/design-system'
import { MiniSparkline, seededWalk } from '@/components/landing/home/_shared'

export default function Hero() {
  const equity = useMemo(() => seededWalk(101, 60, 8, 120), [])
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(34,197,94,0.10), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />
      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow>The Trading Platform</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Professional trading infrastructure <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">for modern AI trading businesses.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Build, test, execute, analyse and scale trading strategies inside a unified AI-powered platform designed for businesses, educators and white-label providers.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#trading-workspace" size="lg">Experience the platform</GradientButton>
              <GhostButton href="/get-started" size="lg" trailingIcon>
                <PlayCircle className="h-4 w-4" /> Schedule demo
              </GhostButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {['Multi-asset', 'AI-integrated', 'White-label ready', 'API + SDK'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Terminal mock */}
          <div className="lg:col-span-6">
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden relative">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(34,197,94,0.10), transparent 70%)' }} />
              {/* window chrome */}
              <div className="relative flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5 mb-3">
                <div className="h-2 w-2 rounded-full bg-red-500/70" />
                <div className="h-2 w-2 rounded-full bg-amber-500/70" />
                <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
                <div className="ml-3 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
                  app.kalkiai.trade/terminal
                </div>
                <div className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 pr-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
                </div>
              </div>
              <div className="relative grid grid-cols-3 gap-3">
                {/* watchlist */}
                <div className="col-span-1 rounded-lg border border-white/10 bg-black/40 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Watchlist</div>
                  <div className="space-y-1.5 font-mono text-[10.5px]">
                    {[
                      { s: 'ES',   p: '4,813.25', d: '+0.42%', up: true },
                      { s: 'NQ',   p: '17,540.5', d: '+0.61%', up: true },
                      { s: 'CL',   p: '78.14',   d: '-1.20%', up: false },
                      { s: 'GC',   p: '2,412.7', d: '+0.08%', up: true },
                      { s: 'BTC',  p: '68,214',  d: '-2.10%', up: false },
                    ].map((r) => (
                      <div key={r.s} className="flex items-center justify-between">
                        <span className="text-white w-8">{r.s}</span>
                        <span className="text-zinc-300">{r.p}</span>
                        <span className={r.up ? 'text-emerald-400' : 'text-rose-400'}>{r.d}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* chart */}
                <div className="col-span-2 rounded-lg border border-white/10 bg-black/40 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-white font-semibold text-sm">ES</span>
                      <span className="text-emerald-400 font-mono text-[11px]">4,813.25</span>
                      <span className="text-emerald-400 font-mono text-[10px]">+0.42%</span>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500">1m</span>
                  </div>
                  <MiniSparkline data={equity} width={320} height={120} stroke="#22c55e" className="w-full h-[120px]" />
                </div>
              </div>
              {/* AI insight */}
              <div className="relative mt-3 rounded-lg border border-brand-gold/25 bg-brand-gold/[0.06] p-3 flex items-start gap-2">
                <Bot className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
                <div className="flex-1 text-[12px] text-zinc-100 leading-relaxed">
                  <span className="font-semibold text-brand-gold">Mentor:</span> Range compression on 1m + 5m; wait for opening drive volume to confirm before firing the breakout.
                  <div className="mt-1 text-[10px] font-mono text-zinc-500">2 citations · confidence 0.82 · module=terminal, ticker=ES</div>
                </div>
              </div>
              <div className="relative mt-3 grid grid-cols-4 gap-2 text-[10.5px]">
                {[
                  { k: 'P&L today', v: '+$2,148', tone: 'green' },
                  { k: 'Positions',  v: '4',       tone: 'orange' },
                  { k: 'Open ord.',  v: '2',       tone: 'blue' },
                  { k: 'DD',         v: '-1.2%',   tone: 'orange' },
                ].map((s) => (
                  <div key={s.k} className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5">
                    <div className={`text-[9px] font-mono uppercase tracking-wider ${s.tone === 'green' ? 'text-emerald-400' : s.tone === 'orange' ? 'text-brand-gold' : 'text-sky-400'}`}>{s.k}</div>
                    <div className="text-sm font-semibold text-white">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
